import React, { useState, useEffect, useCallback } from "react";
import {
  FaCheckCircle, FaExclamationTriangle, FaTimesCircle,
  FaBuilding, FaTimes, FaChevronDown, FaChevronRight,
  FaMoneyBillWave, FaRobot, FaInbox,
} from "react-icons/fa";
import { API_BASE } from "../../api/config";
import ZuordnenDialog from "./ZuordnenDialog";

const API = API_BASE;

const fmt = (v) => {
  if (v == null || v === 0) return "–";
  return Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
};
const fmtDate = (d) => {
  if (!d) return "–";
  const p = d.split("-");
  if (p.length === 3) return `${p[2]}.${p[1]}.${p[0].slice(2)}`;
  return d;
};
const MONAT_NAMEN = ["", "Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

function StatusBadge({ status }) {
  const map = {
    bezahlt:   { icon: <FaCheckCircle />,        cls: "badge-green",  label: "Bezahlt" },
    teilweise: { icon: <FaExclamationTriangle />, cls: "badge-amber",  label: "Teilweise" },
    offen:     { icon: <FaTimesCircle />,         cls: "badge-red",    label: "Offen" },
    leerstand: { icon: <FaBuilding />,            cls: "badge-gray",   label: "Leerstand" },
  };
  const s = map[status] || map.offen;
  return <span className={`me-badge ${s.cls}`}>{s.icon} {s.label}</span>;
}

/* ────────────────────────────────────────────────────────────────────────── */
export default function Tab1Zahlungen() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterObjekt, setFilterObjekt] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterTyp, setFilterTyp] = useState("Miete");
  const [viewJahr, setViewJahr] = useState(null);
  const [viewMonat, setViewMonat] = useState(null);
  const [expandedObj, setExpandedObj] = useState({});
  const [showLeerstand, setShowLeerstand] = useState(false);

  const [colFilterMv, setColFilterMv] = useState("");
  const [colFilterName, setColFilterName] = useState("");

  // ── Welle 83: Mieten aus Umsätzen ──────────────────────────────────────
  const [offen, setOffen] = useState(null);            // {zeilen, anzahl, anzahl_unkategorisiert}
  const [showOffen, setShowOffen] = useState(false);
  const [showUnkat, setShowUnkat] = useState(false);
  const [showAuto, setShowAuto] = useState(false);
  const [autoCfg, setAutoCfg] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoMeldung, setAutoMeldung] = useState("");
  const [zuordnen, setZuordnen] = useState(null);

  const ladeOffen = useCallback(async () => {
    try {
      const res = await fetch(`${API}/mietverwaltung/api/zahlungen-auto/offen${filterObjekt ? `?objekt=${filterObjekt}` : ""}`);
      const d = await res.json();
      if (res.ok) setOffen(d);
    } catch (e) { console.error("offen:", e); }
  }, [filterObjekt]);
  const ladeAutoCfg = useCallback(async () => {
    try {
      const res = await fetch(`${API}/mietverwaltung/api/zahlungen-auto/config`);
      const d = await res.json();
      if (res.ok) setAutoCfg(d);
    } catch (e) { console.error("auto-config:", e); }
  }, []);
  useEffect(() => { ladeOffen(); ladeAutoCfg(); }, [ladeOffen, ladeAutoCfg]);

  const autoSpeichern = async (patch) => {
    setAutoBusy(true);
    try {
      const res = await fetch(`${API}/mietverwaltung/api/zahlungen-auto/config`, { method: "PUT",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch) });
      const d = await res.json();
      if (res.ok) { setAutoCfg((c) => ({ ...c, ...d })); setAutoMeldung("Einstellungen gespeichert."); }
    } catch (e) { setAutoMeldung(String(e.message || e)); }
    setAutoBusy(false);
  };
  const autoLauf = async (dry) => {
    setAutoBusy(true); setAutoMeldung(dry ? "Probelauf läuft…" : "Lauf läuft…");
    try {
      const res = await fetch(`${API}/mietverwaltung/api/zahlungen-auto/jetzt${dry ? "?dry=1" : ""}`, { method: "POST" });
      const d = await res.json();
      if (!res.ok || d.error) throw new Error(d.error || d.detail || "Lauf fehlgeschlagen");
      setAutoMeldung(`${dry ? "Probelauf" : "Lauf"}: ${d.zugeordnet} zugeordnet, ${d.offen} offen, ${d.fehler} Fehler` +
        (d.neu_soll ? `, ${d.neu_soll} neue Soll-Zeilen` : "") + (d.split ? `, ${d.split} Split` : "") +
        (d.soll_ergaenzt ? `, ${d.soll_ergaenzt} Monate ergänzt` : "") + (dry ? " (nichts gespeichert)" : ""));
      if (!dry) { ladeOffen(); ladeAutoCfg(); loadData(); }
    } catch (e) { setAutoMeldung(String(e.message || e)); }
    setAutoBusy(false);
  };
  const autoLernen = async () => {
    setAutoBusy(true);
    try {
      const res = await fetch(`${API}/mietverwaltung/api/zahlungen-auto/lernen`, { method: "POST" });
      const d = await res.json();
      setAutoMeldung(`Gelernt: ${d.iban_adressen} IBAN aus Adressen, ${d.iban_excel} IBAN aus Excel-Zahlungen, ${d.subkat} SubKat, ${d.name} Namen (${d.gesamt} Regeln gesamt).`);
    } catch (e) { setAutoMeldung(String(e.message || e)); }
    setAutoBusy(false);
  };
  const offenZeilen = (offen?.zeilen || []).filter((z) => showUnkat || !z.unkategorisiert);

  // Drawer
  const [drawer, setDrawer] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Dashboard KPIs + verfügbare Jahre
  const [dashboard, setDashboard] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Dashboard für Jahre/Objekte
      const dRes = await fetch(`${API}/mietverwaltung/api/dashboard${filterObjekt ? `?objekt=${filterObjekt}` : ""}`);
      const dData = await dRes.json();
      if (dData.status === "ok") {
        setDashboard(dData);
        if (!viewJahr && dData.aktuell) setViewJahr(dData.aktuell.jahr);
        if (!viewMonat && dData.aktuell) setViewMonat(dData.aktuell.monat);
      }

      // Mieter-Status für die Tabelle
      const p = new URLSearchParams();
      const j = viewJahr || dData?.aktuell?.jahr;
      const m = viewMonat || dData?.aktuell?.monat;
      if (j) p.set("jahr", j);
      if (m) p.set("monat", m);
      if (filterObjekt) p.set("objekt", filterObjekt);
      if (filterTyp) p.set("typ", filterTyp);

      const res = await fetch(`${API}/mietverwaltung/api/mieter-status?${p}`);
      const d = await res.json();
      if (d.status === "ok") {
        setData(d);
        if (!viewJahr) setViewJahr(d.jahr);
        if (!viewMonat) setViewMonat(d.monat);
      }
    } catch (e) { console.error("Tab1Zahlungen:", e); }
    setLoading(false);
  }, [viewJahr, viewMonat, filterObjekt, filterTyp]);

  useEffect(() => { loadData(); }, [loadData]);

  // Drawer: Mieter-Detail (daten_mz Historie)
  const openDrawer = async (v) => {
    setDrawer(v);
    setDrawerLoading(true);
    try {
      const res = await fetch(`${API}/mietverwaltung/api/mieter-detail?mietvertrag=${encodeURIComponent(v.mietvertrag)}`);
      const d = await res.json();
      if (d.status === "ok") setDrawerData(d);
    } catch (e) { console.error("MieterDetail:", e); }
    setDrawerLoading(false);
  };
  const closeDrawer = () => { setDrawer(null); setDrawerData(null); };

  // Filterung
  const filtered = React.useMemo(() => {
    if (!data?.mieter) return [];
    let rows = data.mieter;
    if (!showLeerstand) rows = rows.filter(v => v.status !== "leerstand");
    if (filterStatus) rows = rows.filter(v => v.status === filterStatus);
    if (colFilterMv) {
      const f = colFilterMv.toLowerCase();
      rows = rows.filter(v => (v.mietvertrag || "").toLowerCase().includes(f));
    }
    if (colFilterName) {
      const f = colFilterName.toLowerCase();
      rows = rows.filter(v => (v.name || "").toLowerCase().includes(f));
    }
    return rows;
  }, [data, showLeerstand, filterStatus, colFilterMv, colFilterName]);

  // Gruppierung nach Objekt
  const grouped = React.useMemo(() => {
    const g = {};
    for (const v of filtered) {
      const obj = v.objekt || "–";
      if (!g[obj]) g[obj] = [];
      g[obj].push(v);
    }
    return g;
  }, [filtered]);

  const toggleObj = (obj) => setExpandedObj(p => ({ ...p, [obj]: !p[obj] }));

  const stats = data?.stats || {};
  const alleObj = dashboard?.alle_objekte || [];
  const alleJahre = dashboard?.alle_jahre || [];

  if (loading && !data) {
    return <div className="me-loading"><div className="me-spinner" /><p>Zahlungsdaten werden geladen…</p></div>;
  }

  return (
    <>
      {/* ── KPIs ── */}
      <div className="me-kpis">
        <div className="me-kpi-card kpi-bezahlt">
          <span className="me-kpi-label">Bezahlt</span>
          <span className="me-kpi-value green">{stats.bezahlt ?? 0}</span>
        </div>
        <div className="me-kpi-card kpi-teilweise">
          <span className="me-kpi-label">Teilweise</span>
          <span className="me-kpi-value amber">{stats.teilweise ?? 0}</span>
        </div>
        <div className="me-kpi-card kpi-offen">
          <span className="me-kpi-label">Offen</span>
          <span className="me-kpi-value red">{stats.offen ?? 0}</span>
        </div>
        <div className="me-kpi-card">
          <span className="me-kpi-label">Leerstand</span>
          <span className="me-kpi-value" style={{ color: "#94a3b8" }}>{stats.leerstand ?? 0}</span>
        </div>
      </div>

      {/* ── Welle 83: Mieten aus Umsätzen ── */}
      <div className="me-table-section" style={{ marginBottom: "1rem" }}>
        <div className="me-table-header">
          <h3><FaInbox style={{ marginRight: 6 }} />Nicht zugeordnete Mieten ({offen?.anzahl ?? "…"})
            {offen?.anzahl_unkategorisiert ? <small style={{ fontWeight: 400, marginLeft: 8, color: "#94a3b8" }}>+ {offen.anzahl_unkategorisiert} unkategorisierte von Mieter-IBANs</small> : null}
          </h3>
          <div className="me-filters">
            <button className="me-btn me-btn-outline" onClick={() => setShowOffen((v) => !v)}>{showOffen ? "Liste ausblenden" : "Liste anzeigen"}</button>
            <button className="me-btn me-btn-outline" onClick={() => setShowAuto((v) => !v)}><FaRobot /> Automatik{autoCfg ? (autoCfg.enabled ? " (an)" : " (aus)") : ""}</button>
          </div>
        </div>
        {autoMeldung && <div style={{ padding: "0.5rem 1rem", fontSize: "0.85rem", color: "#4338ca" }}>{autoMeldung}</div>}
        {showAuto && autoCfg && (
          <div style={{ padding: "0.6rem 1rem 0.9rem", display: "flex", gap: "0.8rem", alignItems: "center", flexWrap: "wrap", fontSize: "0.85rem" }}>
            <label className="me-checkbox-label"><input type="checkbox" checked={!!autoCfg.enabled} disabled={autoBusy}
              onChange={(e) => autoSpeichern({ enabled: e.target.checked })} /> täglich automatisch</label>
            <label>um <input type="time" className="me-select me-select-sm" value={autoCfg.uhrzeit || "03:00"} onChange={(e) => setAutoCfg({ ...autoCfg, uhrzeit: e.target.value })} onBlur={(e) => autoSpeichern({ uhrzeit: e.target.value })} /></label>
            <label>Umsätze ab <input type="date" className="me-select me-select-sm" value={autoCfg.ab_datum || ""} onChange={(e) => setAutoCfg({ ...autoCfg, ab_datum: e.target.value })} onBlur={(e) => autoSpeichern({ ab_datum: e.target.value })} /></label>
            <label title="Zahlungen ab diesem Tag zählen für den Folgemonat">Vorlauf ab Tag <input type="number" min="1" max="31" className="me-select me-select-sm" style={{ width: 64 }} value={autoCfg.vorlauf_tag ?? 25} onChange={(e) => setAutoCfg({ ...autoCfg, vorlauf_tag: Number(e.target.value) })} onBlur={(e) => autoSpeichern({ vorlauf_tag: Number(e.target.value) })} /></label>
            <button className="me-btn me-btn-outline" disabled={autoBusy} onClick={() => autoLauf(true)}>Probelauf</button>
            <button className="me-btn me-btn-primary" disabled={autoBusy} onClick={() => autoLauf(false)}>Jetzt laufen lassen</button>
            <button className="me-btn me-btn-outline" disabled={autoBusy} onClick={autoLernen} title="IBAN/Namen aus Adressen und bisherigen Excel-Zahlungen als Regeln merken">Regeln lernen</button>
            <span style={{ color: "#94a3b8" }}>
              letzter Lauf: {autoCfg.letzter_lauf ? `${String(autoCfg.letzter_lauf.gestartet_am).replace("T", " ").slice(0, 16)} — ${autoCfg.letzter_lauf.meldung || autoCfg.letzter_lauf.status}` : "noch keiner"}
            </span>
          </div>
        )}
        {showOffen && (
          <div className="me-table-wrap" style={{ maxHeight: "45vh", overflowY: "auto" }}>
            <div style={{ padding: "0.4rem 1rem", fontSize: "0.83rem" }}>
              <label className="me-checkbox-label"><input type="checkbox" checked={showUnkat} onChange={(e) => setShowUnkat(e.target.checked)} /> auch unkategorisierte Umsätze von Mieter-IBANs zeigen (werden nie automatisch verbucht)</label>
            </div>
            <table className="me-table">
              <thead>
                <tr><th>Buchung</th><th className="num">Betrag</th><th>Zahler</th><th>Verwendungszweck</th><th>Objekt</th><th>Monat</th><th>Automatik</th><th></th></tr>
              </thead>
              <tbody>
                {offenZeilen.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", color: "#94a3b8", padding: "1rem" }}>Alles zugeordnet.</td></tr>}
                {offenZeilen.map((z) => {
                  const v = z.vorschlag || {};
                  return (
                    <tr key={z.daten_id} style={z.unkategorisiert ? { opacity: 0.75 } : undefined}>
                      <td>{fmtDate(z.buchungstag)}</td>
                      <td className="num">{fmt(z.betrag)}</td>
                      <td>{z.beguenstigter}</td>
                      <td title={z.verwendungszweck} style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{z.verwendungszweck}</td>
                      <td>{z.objekt || "–"}{z.subkat ? <small style={{ color: "#94a3b8" }}> / {z.subkat}</small> : null}
                        {z.fremdkategorie ? <small style={{ color: "#b45309", display: "block" }} title="In der Bank-Tabelle nicht als Miete kategorisiert — Zuordnung nur über Zweck/Name">Kat.: {z.kategorie}</small> : null}</td>
                      <td>{z.monat}</td>
                      <td style={{ fontSize: "0.8rem" }}>
                        {v.mietvertrag_nr ? <span>{v.mietvertrag_nr} · {v.name}</span>
                          : v.kandidaten?.length ? <span style={{ color: "#b45309" }}>mehrdeutig: {v.kandidaten.map((k) => k.mietvertrag_nr).join(", ")}</span>
                          : <span style={{ color: "#94a3b8" }}>{v.grund || "kein Treffer"}</span>}
                      </td>
                      <td><button className="me-btn me-btn-outline" onClick={() => setZuordnen(z)}>Zuordnen</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Filterleiste ── */}
      <div className="me-table-section">
        <div className="me-table-header">
          <h3>
            <FaMoneyBillWave style={{ marginRight: 6 }} />
            Zahlungen – {MONAT_NAMEN[viewMonat] || ""} {viewJahr || ""}
            {` (${filtered.length})`}
          </h3>
          <div className="me-filters">
            <select className="me-select me-select-sm" value={viewJahr || ""}
              onChange={(e) => setViewJahr(Number(e.target.value))}>
              {alleJahre.map((j) => <option key={j} value={j}>{j}</option>)}
            </select>
            <select className="me-select me-select-sm" value={viewMonat || ""}
              onChange={(e) => setViewMonat(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{MONAT_NAMEN[m]}</option>
              ))}
            </select>
            <select className="me-select me-select-sm" value={filterObjekt}
              onChange={(e) => setFilterObjekt(e.target.value)}>
              <option value="">Alle Objekte</option>
              {alleObj.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <select className="me-select me-select-sm" value={filterTyp}
              onChange={(e) => setFilterTyp(e.target.value)}>
              <option value="Miete">Miete</option>
              <option value="Nebenkosten">Nebenkosten</option>
              <option value="Kaution">Kaution</option>
              <option value="">Alle Typen</option>
            </select>
            <select className="me-select me-select-sm" value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">Alle Status</option>
              <option value="bezahlt">Bezahlt</option>
              <option value="teilweise">Teilweise</option>
              <option value="offen">Offen</option>
              <option value="leerstand">Leerstand</option>
            </select>
            <label className="me-checkbox-label">
              <input type="checkbox" checked={showLeerstand}
                onChange={(e) => setShowLeerstand(e.target.checked)} />
              Leerstand
            </label>
          </div>
        </div>

        {/* ── Tabelle ── */}
        <div className="me-table-wrap" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <table className="me-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>MV-Nr</th>
                <th>Mieter</th>
                <th>Seit</th>
                <th>Status</th>
                <th className="num">Akt. Miete</th>
                <th className="num">Soll</th>
                <th className="num">Ist</th>
                <th className="num">Diff</th>
                <th className="num">Konto</th>
              </tr>
              <tr className="me-filter-row">
                <th></th>
                <th><input className="me-col-filter" placeholder="Filter…" value={colFilterMv} onChange={e => setColFilterMv(e.target.value)} /></th>
                <th><input className="me-col-filter" placeholder="Filter…" value={colFilterName} onChange={e => setColFilterName(e.target.value)} /></th>
                <th></th><th></th><th></th><th></th><th></th><th></th><th></th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(grouped).length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: "center", padding: "2rem", color: "#9ca3af" }}>
                  Keine Daten für {MONAT_NAMEN[viewMonat]} {viewJahr}. DatenMZ hochladen?
                </td></tr>
              ) : (
                Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([obj, mieter]) => (
                  <React.Fragment key={obj}>
                    <tr className="me-group-row" onClick={() => toggleObj(obj)}>
                      <td>{expandedObj[obj] === false ? <FaChevronRight /> : <FaChevronDown />}</td>
                      <td colSpan={9}>
                        <strong>Objekt {obj}</strong>
                        <span className="me-group-count">{mieter.length} Verträge</span>
                        <span className="me-group-sum">
                          Gesamt: {fmt(mieter.reduce((s, m) => s + (m.soll || 0), 0))}
                          {" · "}Zahlung: {fmt(mieter.reduce((s, m) => s + (m.ist || 0), 0))}
                          {" · "}Diff: {fmt(mieter.reduce((s, m) => s + (m.opos || 0), 0))}
                          {" · "}Konto: {fmt(mieter.reduce((s, m) => s + (m.kum_opos || 0), 0))}
                        </span>
                      </td>
                    </tr>
                    {expandedObj[obj] !== false && mieter.map((v, i) => (
                      <tr key={`${v.mietvertrag}-${i}`}
                        className={`me-mv-row ${v.status === "offen" ? "row-fehlend" : v.status === "teilweise" ? "row-teil" : ""}`}
                        onClick={() => openDrawer(v)}
                        style={{ cursor: "pointer" }}>
                        <td></td>
                        <td className="me-mv-id">{v.mietvertrag}</td>
                        <td>{v.name}</td>
                        <td style={{ fontSize: "0.82em", color: "#94a3b8" }}>{fmtDate(v.vertrag_beginn)}</td>
                        <td><StatusBadge status={v.status} /></td>
                        <td className="num">{fmt(v.aktuelle_miete)}</td>
                        <td className="num">{fmt(v.soll)}</td>
                        <td className="num">{fmt(v.ist)}</td>
                        <td className={`num ${(v.opos || 0) < -0.01 ? "neg" : (v.opos || 0) > 0.01 ? "pos" : ""}`}>
                          {fmt(v.opos)}
                        </td>
                        <td className={`num ${(v.kum_opos || 0) < -0.01 ? "neg" : (v.kum_opos || 0) > 0.01 ? "pos" : ""}`}
                          title={`Kumuliert über ${v.monate_total || 0} Monate: Soll ${fmt(v.kum_soll)} · Ist ${fmt(v.kum_ist)}`}>
                          {fmt(v.kum_opos)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Drawer: Mieter-Detail ── */}
      {drawer && (
        <div className="me-drawer-overlay" onClick={closeDrawer}>
          <div className="me-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="me-drawer-header">
              <div>
                <h3>{drawer.name || drawer.mietvertrag}</h3>
                <span className="me-drawer-sub">
                  {drawer.mietvertrag} · Objekt {drawer.objekt}
                </span>
              </div>
              <button className="me-btn me-btn-icon" onClick={closeDrawer}><FaTimes /></button>
            </div>

            {drawerLoading ? (
              <div className="me-loading"><div className="me-spinner" /><p>Lade Details…</p></div>
            ) : drawerData ? (
              <div className="me-drawer-body">
                {/* Vertragsdaten + Kumulatives Konto */}
                <div className="me-drawer-section">
                  <h4>Vertragsdaten</h4>
                  <div className="me-drawer-grid">
                    <span>Mieter:</span><span>{drawerData.stammdaten?.name || drawer.name || "–"}</span>
                    <span>Objekt:</span><span>{drawerData.stammdaten?.objekt || drawer.objekt || "–"}</span>
                    <span>Vertrag seit:</span><span>{fmtDate(drawer.vertrag_beginn)}</span>
                    <span>Vertrag bis:</span><span>{fmtDate(drawer.vertrag_ende)}</span>
                    <span>Akt. Miete:</span><span>{fmt(drawer.aktuelle_miete)}</span>
                    <span>Gültig ab:</span><span>{fmtDate(drawer.miete_gueltig_ab)}</span>
                    <span>Mieterhöhungen:</span><span>{drawer.mieterhöhungen || 0}</span>
                    <span>Buchungen:</span><span>{drawerData.stammdaten?.buchungen || 0}</span>
                  </div>
                </div>

                {/* Kumulatives Konto */}
                {drawer.kum_soll != null && (
                  <div className="me-drawer-section">
                    <h4>Kontostand (kumuliert über {drawer.monate_total || 0} Monate)</h4>
                    <div className="me-drawer-grid">
                      <span>Gesamt SOLL:</span><span>{fmt(drawer.kum_soll)}</span>
                      <span>Gesamt IST:</span><span>{fmt(drawer.kum_ist)}</span>
                      <span>OPOS kumuliert:</span>
                      <span className={(drawer.kum_opos || 0) > 0 ? "neg" : (drawer.kum_opos || 0) < 0 ? "pos" : ""}>
                        <strong>{fmt(drawer.kum_opos)}</strong>
                        {(drawer.kum_opos || 0) <= 0
                          ? " ✓ Konto ausgeglichen"
                          : " — offene Forderung"}
                      </span>
                    </div>
                  </div>
                )}

                {/* Jahresübersicht */}
                {drawerData.pro_jahr?.length > 0 && (
                  <div className="me-drawer-section">
                    <h4>Jahresübersicht</h4>
                    <div className="me-drawer-table-wrap">
                      <table className="me-table me-table-compact">
                        <thead>
                          <tr>
                            <th>Jahr</th>
                            <th className="num">Miete SOLL</th>
                            <th className="num">Miete IST</th>
                            <th className="num">OPOS</th>
                            <th className="num">NK SOLL</th>
                            <th className="num">NK IST</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drawerData.pro_jahr.map((j) => (
                            <tr key={j.jahr}>
                              <td><strong>{j.jahr}</strong></td>
                              <td className="num">{fmt(j.miete_soll)}</td>
                              <td className="num">{fmt(j.miete_ist)}</td>
                              <td className={`num ${(j.miete_opos || 0) > 0 ? "neg" : ""}`}>{fmt(j.miete_opos)}</td>
                              <td className="num">{fmt(j.nka_soll)}</td>
                              <td className="num">{fmt(j.nka_ist)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Kaution */}
                {drawerData.kaution?.length > 0 && (
                  <div className="me-drawer-section">
                    <h4>Kaution</h4>
                    <div className="me-drawer-table-wrap">
                      <table className="me-table me-table-compact">
                        <thead>
                          <tr>
                            <th>Typ</th>
                            <th className="num">Betrag</th>
                            <th>Datum</th>
                            <th>Bemerkung</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drawerData.kaution.map((k, i) => (
                            <tr key={i}>
                              <td>{k.typ}</td>
                              <td className="num">{fmt(k.soll_betrag)}</td>
                              <td>{k.datum || "–"}</td>
                              <td>{k.verwendungszweck || "–"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Alle Buchungen */}
                {drawerData.buchungen?.length > 0 && (
                  <div className="me-drawer-section">
                    <h4>Alle Buchungen ({drawerData.buchungen.length})</h4>
                    <div className="me-drawer-table-wrap" style={{ maxHeight: "40vh" }}>
                      <table className="me-table me-table-compact">
                        <thead>
                          <tr>
                            <th>Monat</th>
                            <th>Typ</th>
                            <th className="num">Soll</th>
                            <th className="num">Ist</th>
                            <th className="num">Diff</th>
                            <th>Vzweck</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drawerData.buchungen.map((b, i) => {
                            const diff = (b.zahlbetrag || 0) - (b.soll_betrag || 0);
                            return (
                            <tr key={i} className={diff < -0.01 ? "row-fehlend" : ""}>
                              <td>{MONAT_NAMEN[b.monat] || b.monat}/{b.jahr}</td>
                              <td><span className="me-typ-label">{b.typ}</span></td>
                              <td className="num">{fmt(b.soll_betrag)}</td>
                              <td className="num">{fmt(b.zahlbetrag)}</td>
                              <td className={`num ${diff < -0.01 ? "neg" : diff > 0.01 ? "pos" : ""}`}>{fmt(diff)}</td>
                              <td className="me-vzweck">{b.verwendungszweck || "–"}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
      {zuordnen && (
        <ZuordnenDialog zeile={zuordnen} onClose={() => setZuordnen(null)}
          onDone={(d) => {
            setZuordnen(null);
            setAutoMeldung(d.mietvertrag_nr ? `Zugeordnet: ${d.mietvertrag_nr} (${d.monat})${d.weitere_zugeordnet ? `, ${d.weitere_zugeordnet} weitere über die neue Regel` : ""}` : "Umsatz ignoriert.");
            ladeOffen(); loadData();
          }} />
      )}
    </>
  );
}
